/**
 * Shared registry filter state for artifact listing/search UIs
 */

import { useCallback, useMemo, useState } from 'react';
import type { Artifact } from '../types/api';
import {
  applyRegistryFilters,
  countActiveRegistryFilters,
  DEFAULT_REGISTRY_FILTERS,
  getRegistryFilterOptions,
  hasRegistryMeta,
  type RegistryFilters,
  type RegistryGroupBy,
} from '../utils/registryArtifactUtils';

export function useRegistryArtifactFilters(artifacts: Artifact[]) {
  const [filters, setFilters] = useState<RegistryFilters>(DEFAULT_REGISTRY_FILTERS);
  const [groupBy, setGroupBy] = useState<RegistryGroupBy>('none');

  const options = useMemo(() => getRegistryFilterOptions(artifacts), [artifacts]);
  const registryArtifactCount = useMemo(
    () => artifacts.filter(hasRegistryMeta).length,
    [artifacts],
  );
  const filteredArtifacts = useMemo(
    () => applyRegistryFilters(artifacts, filters),
    [artifacts, filters],
  );
  const activeFilterCount = useMemo(() => countActiveRegistryFilters(filters), [filters]);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_REGISTRY_FILTERS);
  }, []);

  return {
    filters,
    setFilters,
    groupBy,
    setGroupBy,
    options,
    registryArtifactCount,
    filteredArtifacts,
    activeFilterCount,
    clearFilters,
  };
}
